import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { RefreshCwIcon, TriangleAlertIcon } from 'lucide-react';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SortableList } from '@modules/_shared/components/sortable-list/SortableList';
import type { ReorderEvent } from '@modules/_shared/components/sortable-list/types';
import {
  CLEAR_PRICE_EVOLUTION_CACHE,
  DELETE_CMA_REPORTS_PROPERTY_TRANSACTIONS,
  REORDER_CMA_REPORTS_PROPERTY_TRANSACTIONS,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import TransactionCard from '@modules/reports/cma-reports/step-transaction/TransactionCard';
import type { CmaPropertyTransaction } from '@modules/reports/cma-reports/step-transaction/types';
import { useLocale } from '@shared/hooks/locale';

import { isPriceEvolutionRateStale } from './utils/priceEvolutionCache';
import { refetchTransactionsUnlessReturned } from './utils/transactionsRefetch';

type TransactionsListSectionProps = {
  propertyTransactions: CmaPropertyTransaction[];
  cmaReportId: string;
  adjustPriceByEvolution: boolean;
  isExternalHydrationLoading: boolean;
};

const TransactionsListSection = ({
  propertyTransactions,
  cmaReportId,
  adjustPriceByEvolution,
  isExternalHydrationLoading,
}: TransactionsListSectionProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [reorderTransaction] = useMutation(
    REORDER_CMA_REPORTS_PROPERTY_TRANSACTIONS,
    { onError: setErrorLike },
  );

  const handleReorder = useCallback(
    ({ items, movedItem, toIndex }: ReorderEvent<CmaPropertyTransaction>) => {
      const order = new Map(items.map((t, i) => [t.id, i]));

      reorderTransaction({
        variables: {
          cma_report_id: cmaReportId,
          cma_property_transaction_id: movedItem.id,
          order_nr: toIndex,
        },
        optimisticResponse: {
          __typename: 'mutation_root',
          update_cma_reports_property_transactions_by_pk: {
            __typename: 'cma_reports_property_transactions',
            id: movedItem.id,
            order_nr: toIndex,
            cma_report_id: cmaReportId,
            transaction_id: movedItem.transaction_id ?? null,
            created_at: movedItem.created_at,
          },
          update_cma_reports_by_pk: {
            __typename: 'cma_reports',
            id: cmaReportId,
            updated_at: new Date().toISOString(),
          },
        },
        update: (cache, { data }) => {
          if (!data?.update_cma_reports_property_transactions_by_pk) {
            return;
          }
          cache.modify({
            id: cache.identify({
              __typename: 'cma_reports',
              id: cmaReportId,
            }),
            fields: {
              cma_property_transactions(existingRefs = [], { readField }) {
                return [...existingRefs].sort((a, b) => {
                  const aId = readField('id', a) as string;
                  const bId = readField('id', b) as string;
                  return (order.get(aId) ?? 0) - (order.get(bId) ?? 0);
                });
              },
            },
          });
        },
      });
    },
    [cmaReportId, reorderTransaction],
  );

  const [deleteTransaction] = useMutation(
    DELETE_CMA_REPORTS_PROPERTY_TRANSACTIONS,
    { onError: setErrorLike },
  );

  const [clearPriceEvolutionCache, { loading: isClearingCache }] = useMutation(
    CLEAR_PRICE_EVOLUTION_CACHE,
    { onError: setErrorLike },
  );

  const isCacheStale = useMemo(() => {
    const today = new Date();
    return propertyTransactions.some(cacheData =>
      isPriceEvolutionRateStale(
        cacheData.price_evolution_rate,
        cacheData.price_evolution_rate_updated_at,
        today,
      ),
    );
  }, [propertyTransactions]);

  const handleClearCache = useCallback(async () => {
    await clearPriceEvolutionCache({
      variables: { cma_report_id: cmaReportId },
    });
  }, [clearPriceEvolutionCache, cmaReportId]);

  const handleRemoveById = useCallback(
    async (savedTransactionId: string) => {
      await deleteTransaction({
        variables: {
          cma_property_transaction_id: savedTransactionId,
          cma_report_id: cmaReportId,
        },
        refetchQueries: refetchTransactionsUnlessReturned(cmaReportId),
        awaitRefetchQueries: true,
      });
    },
    [cmaReportId, deleteTransaction],
  );

  return (
    <>
      <Stack
        sx={{
          gap: 1,
          flexGrow: 1,
          height: 0,
        }}
      >
        {adjustPriceByEvolution && isCacheStale && (
          <Alert severity="warning" icon={<TriangleAlertIcon />} sx={{ mt: 1 }}>
            <Stack
              sx={{
                gap: 1,
                alignItems: 'flex-start',
              }}
            >
              {t(
                'Price evolution data may be outdated. Click refresh to get the latest data.',
              )}
              <Button
                color="inherit"
                size="small"
                onClick={handleClearCache}
                loading={isClearingCache}
                loadingPosition="start"
                startIcon={<RefreshCwIcon />}
              >
                {t('Refresh Price Data')}
              </Button>
            </Stack>
          </Alert>
        )}

        <Stack
          sx={{
            gap: 2,
            mt: 1,
          }}
        >
          {propertyTransactions.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: (theme.vars ?? theme).palette.text.secondary,
                ...theme.typography.body2,
                mt: 3,
              }}
            >
              {t('No transactions selected')}
            </Box>
          ) : (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: theme.typography.fontWeightBold,
                }}
              >
                {t('{{count}} Selected transactions', {
                  count: propertyTransactions.length,
                })}
              </Typography>

              <SortableList
                items={propertyTransactions}
                onReorder={handleReorder}
                gap={2}
                renderItem={({ item, index, isDragOverlay }) => (
                  <TransactionCard
                    key={item.id}
                    cmaPropertyTransaction={item}
                    index={index}
                    onRemoveById={handleRemoveById}
                    isDragOverlay={isDragOverlay}
                    isExternalHydrationLoading={isExternalHydrationLoading}
                  />
                )}
              />
            </>
          )}
        </Stack>
      </Stack>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};

export default TransactionsListSection;
